import {
  type Account,
  chainTypeEntries,
  combineAccountWithWallet,
  makeNewBrowserExtensionAccount,
  validateWallet,
  type WalletAccount,
  type WalletGroupedAccounts,
  walletGroupedAccounts,
} from '@polaris/core/models'
import { atom } from 'jotai'
import { balancesSummaryAtom } from '../balances/atom'
import { walletKeyringAtom, walletOptionsAtom } from '../wallets/atoms'
import { accountsAtom, isLocalStateModeAtom, userAccountsAtom } from './atoms'

// This file combines accounts with wallets and balances.
// This file is necessary to avoid circular dependencies and use a one way data flow.

type WalletAccounts = {
  accountsWithWallets: (Account | WalletAccount)[]
  groupedAccounts: WalletGroupedAccounts[]
}

type IgnoredWalletAccounts = {
  ignoredAccountsWithWallets: (Account | WalletAccount)[]
  ignoredGroupedAccounts: WalletGroupedAccounts[]
}

export const walletAccountsAtom = atom<WalletAccounts & IgnoredWalletAccounts>(
  (get) => {
    const accounts: Account[] = get(accountsAtom)
    const walletKeyring = get(walletKeyringAtom)
    const walletOptions = get(walletOptionsAtom)
    const balancesSummary = get(balancesSummaryAtom)

    const accountsWithWallets = accounts
      .filter((account) => account.account_status !== 'ignored')
      .map((account) =>
        combineAccountWithWallet(account, walletKeyring, walletOptions),
      )

    const ignoredAccountsWithWallets = accounts
      .filter(({ account_status }) => account_status === 'ignored')
      .map((account) =>
        combineAccountWithWallet(account, walletKeyring, walletOptions),
      )

    return {
      accountsWithWallets,
      groupedAccounts: walletGroupedAccounts(
        accountsWithWallets,
        balancesSummary,
      ),
      ignoredAccountsWithWallets,
      ignoredGroupedAccounts: walletGroupedAccounts(ignoredAccountsWithWallets),
    }
  },
)

/**
 * New wallet connections with pending account fields populated
 * that are not yet in the accounts state. Reminder: per ecosystem.
 */
export const pendingAccountsAtom = atom<WalletAccounts>((get) => {
  const { accountsWithWallets, ignoredAccountsWithWallets } =
    get(walletAccountsAtom)
  const userAccounts = get(userAccountsAtom)
  const balancesSummary = get(balancesSummaryAtom)
  const walletKeyring = get(walletKeyringAtom)
  const walletOptions = get(walletOptionsAtom)
  const isLocalStateMode = get(isLocalStateModeAtom)

  const pendingAccounts = chainTypeEntries(
    walletOptions.connectedWallets,
  ).reduce<WalletAccount[]>((acc, [chainType, connectedWallet]) => {
    // Avoid considering accounts pending while data is fetching
    if (!isLocalStateMode && userAccounts.isFetching) return acc
    if (!walletOptions.allReady) return acc

    const address = walletKeyring.getAddress(chainType)
    if (
      !address ||
      accountsWithWallets.some(
        (a) => a.address === address && a.type !== 'WatchAddress',
      )
    )
      return acc
    const connectedWalletAccounts = accountsWithWallets.filter<WalletAccount>(
      (a) => 'wallet' in a,
    )
    const ignoredConnectedWalletAccounts =
      ignoredAccountsWithWallets.filter<WalletAccount>((a) => 'wallet' in a)

    /** Is ignored and set in DB. */
    const isIgnored = ignoredConnectedWalletAccounts.some(
      (a) => a.address === address && a.account_status === 'ignored',
    )

    if (isIgnored) return acc
    // Some wallets have race conditions where the logoUrl is not set yet, use that and other validations
    // before exposing a pending account.
    const walletValidation = validateWallet(connectedWallet)

    if (walletValidation !== true) return acc

    // Use acc to include all current wallet accounts
    const existingConnectedAccounts = acc
      .concat(connectedWalletAccounts)
      .concat(ignoredConnectedWalletAccounts)

    const pendingAccount = makeNewBrowserExtensionAccount(
      connectedWallet,
      address,
      chainType,
      // Concat to inherit grouping keys for any existing *connected* accounts
      existingConnectedAccounts,
    )
    return acc.concat({ ...pendingAccount, wallet: connectedWallet })
  }, [])

  return {
    accountsWithWallets: pendingAccounts,
    groupedAccounts: walletGroupedAccounts(pendingAccounts, balancesSummary),
  }
})

export const newlyAddedWalletNameAtom = atom<string | null>(null)
export const hasAddedWalletAtom = atom<boolean>(false)
export const focusedAccountAtom = atom<WalletGroupedAccounts | null>(null)
export const isPrivacyDepositContextAtom = atom<boolean>(false)
